import { ROUTES, ONLINE_STORE_ID } from '../constants';
import { getAuthorizationToken, getTmxId } from '../utils/cookie';
import { makeHeaders } from '../utils/sharedGroupLogic';

export const orderDetailsRequest = (orderId: string, emailId: string) => ({
  url: ROUTES.API.GUEST_ORDER_DETAILS,
  headers: makeHeaders(),
  data: {
    orderDetailsRequest: {
      orderId: orderId.toUpperCase(),
      emailId,
    },
  },
});

// TODO : remove featureSwitch once functionality is done
export const instoreOrderDetailsRequest = (
  orderId: string,
  phoneNumber: string
) => ({
  url: ROUTES.API.INSTORE_ORDER_DETAILS,
  headers: makeHeaders(),
  data: {
    instoreOrderDetailsRequest: {
      orderId,
      phoneNumber,
    },
  },
});

export const orderHistoryRequest = () => ({
  url: ROUTES.API.ONLINE_ORDER_HISTORY,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: getAuthorizationToken(),
    TMXProfileID: getTmxId(),
  },
});

export const orderHistoryOrderDetailsRequest = (orderId: String) => ({
  url: `${ROUTES.API.AUTH_ORDER_DETAILS}/${orderId}`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: getAuthorizationToken(),
    TMXProfileID: getTmxId(),
  },
});

export const eReceiptsSummaryRequest = () => ({
  url: ROUTES.API.ERECEIPTS_SUMMARY,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: getAuthorizationToken(),
    TMXProfileID: getTmxId(),
  },
});

export const eReceiptsDetailRequest = (
  receiptNumber: string,
  date: string,
  status: string
) => ({
  url: ROUTES.API.ERECEIPTS_DETAIL,
  data: {
    ereceiptDetailRequest: {
      date,
      status,
      receiptNumber,
    },
  },
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: getAuthorizationToken(),
    TMXProfileID: getTmxId(),
  },
});

export const isOnlineCancel = (storeId: string) => storeId === ONLINE_STORE_ID;

export const cancelOrderRequest = (
  orderId: string,
  versionId: string,
  lines: [Object],
  storeId: string = ONLINE_STORE_ID
) => ({
  url: ROUTES.API.CANCEL_ITEMS,
  data: {
    // isError: true,
    cancelOrderRequest: {
      isCancel: isOnlineCancel(storeId),
      orderId,
      orderVersion: versionId,
      storeId, // 8119 for STH & BOSS
      lines,
    },
  },
});

export const remorseCancelRequest = (
  orderId: string,
  emailId: string,
) => ({
  url: ROUTES.API.CANCEL_REQUEST_ACTION,
  data: {
    // isError: true,
    remorseCancelRequest: {
      orderId,
      emailId,
    },
  },
});

export const orderEligibilityRequest = (emailId: string, orderId: string) => ({
  url: ROUTES.API.RETURN_ELIGIBILITY,
  data: {
    orderEligibilityRequest: {
      emailId,
      orderId,
    },
  },
});

export const ssrHrtvPolicyValidatorRequest = (
  orderId: string,
  state: Object
) => {
  const {
    ssrEligibility: {
      mappedData: { hrtvItemPayload, reasonCodes, et },
    },
    ssrItemsOptions,
  } = state;
  const { selectedIds } = ssrItemsOptions;

  selectedIds.forEach((id: string) => {
    hrtvItemPayload[id].rtvReasonCd = reasonCodes[ssrItemsOptions[id].reason];
  });

  const ssrHrtvReqLineItem = selectedIds.map(
    (id: string) => hrtvItemPayload[id]
  );

  const payload: Object = {
    url: ROUTES.API.RETURN_HRTV,
    data: {
      ssrHrtvRequest: {
        orderId,
        et,
        ssrHrtvReqLineItem,
      },
    },
  };
  return payload;
};

export const buildReturnCreateItemPayload = (
  idList: Array,
  itemData: Object,
  reasonCodes: Object
) =>
  idList.map((id: string) => ({
    lineItemId: id,
    poNumber: itemData[id].poNumber,
    sku: itemData[id].sku,
    returnQuantity: itemData[id].qty,
    returnReasonCode: reasonCodes[itemData[id].reason],
    rtvTokenId: itemData[id].rtvTokenId,
    returnDispCode: itemData[id].returnDispCode,
    returnPolicyId: itemData[id].returnPolicyId,
  }));

export const ssrReturnCreateRequest = (
  orderId: string,
  emailId: string,
  returnService: Object
) => {
  const {
    ssrItemsOptions,
    // returnCreateItemPayload: lineItems,
    ssrEligibility: {
      actionData: {
        ssrCreateBillingAddress: billingAddress,
        ssrCreateShipFromAddress: shipFromAddress,
        mVendorNumber,
      },
      mappedData: { reasonCodes },
    },
    ssrHrtv: {
      actionData: { ssrCreateShipToAddress: shipToAddress },
    },
  } = returnService;

  const { totalBoxes, totalWeight, eligibleIds, et } = ssrItemsOptions;

  const lineItems = buildReturnCreateItemPayload(
    eligibleIds,
    ssrItemsOptions,
    reasonCodes
  );

  return {
    url: ROUTES.API.RETURN_CREATE,
    data: {
      order: {
        orderId,
        et,
        emailId,
        billingAddress,
        shipments: [
          {
            itemWeight: totalWeight.toString(),
            mVendorNumber,
            noOfBoxes: totalBoxes.toString(),
            lineItems,
            shipFromAddress,
            shipToAddress,
          },
        ],
      },
    },
  };
};

export const shipmentTrackingDetailRequest = (
  providerCode: String,
  trackingNumber: String
) =>
  `${
    ROUTES.API.SHIPMENT_TRACKING
  }?providerCode=${providerCode}&trackingNumber=${trackingNumber}`;

export const shipmentTrackingLineHaulRequest = (shipment: Object) => ({
  url: ROUTES.API.SHIPMENT_TRACKING_LINEHAUL,
  data: {
    lineHaulDetailsRequest: {
      shipments: {
        shipment,
      },
    },
  },
});

export const shipmentBodfsTrackYourTruckRequest = (
  bodfsDeliveries: Object,
  requestOrderNumber: String
) => {
  const {
    deliveryAddress,
    workOrderNo,
    capacityReservationId,
  } = bodfsDeliveries;

  const payload: Object = {
    data: {
      trackYourTruckRequest: {
        name: `${deliveryAddress.name.firstName || ''} ${deliveryAddress.name
          .lastName || ''}`.trim(),
        workOrderNumber: workOrderNo,
        orderNumber: requestOrderNumber,
        capReservationId: capacityReservationId,
      },
    },
    url: ROUTES.API.BODFS_SHIPMENT_TRACKING,
  };
  return payload;
};

export const availableDeliverySlotsRequest = (
  dlvryPstlCode: string,
  startDate: string,
  endDate: string
) => ({
  url: ROUTES.API.AVAILABLE_DELIVERY_SLOTS,
  data: {
    deliveryCalendarRequest: {
      dlvryPstlCode,
      startDate,
      endDate,
    },
  },
});

export const applianceRescheduleRequest = (
  rescheduleDate: string,
  orderId: string,
  custOrderNumber: string,
  storeNumber: string,
  emailId: string
) => ({
  url: ROUTES.API.APPLIANCE_RESCHEDULE,
  data: {
    applianceRescheduleRequest: {
      rescheduleDate,
      orderId,
      custOrderNumber,
      storeNumber,
      emailId,
    },
  },
});

export const bazaarVoiceUserTokenRequest = () => ({
  url: ROUTES.API.BAZAAR_VOICE_TOKEN,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: getAuthorizationToken(),
    TMXProfileID: getTmxId(),
  },
});

export const applianceCancelEligibilityRequest = (
  orderId: string,
  orderRefNumber: string,
  line: Array
) => ({
  url: ROUTES.API.MA_CANCEL_ELIGIBILITY,
  data: {
    maCancelEligibilityRequest: {
      orderId,
      orderRefNumber,
      lines: {
        line,
      },
    },
  },
});

export const applianceCancelRequest = (
  orderId: string,
  orderRefNumber: string,
  storeId: string,
  orderVersion: string,
  lines: Array
) => ({
  url: ROUTES.API.MA_CANCEL_REQUEST,
  data: {
    cancelOrderRequest: {
      lines,
      orderId,
      orderRefNumber,
      storeId,
      orderVersion,
    },
  },
});
